import type { CurrentCart, LanguageIsoCode } from '@/graphql/graphql';
import {
    addToCart as apiAddToCart,
    removeFromCart as apiRemoveFromCart,
    updateCart as apiUpdateCart,
    getCart,
} from '@/lib/api-cart';
import { useCallback, useEffect, useState } from 'react';
import { CartContext } from './cart-context';
import { useUserData } from './user-data-context';

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [cart, setCart] = useState<CurrentCart | null>(null);
    const { isBuyer } = useUserData();

    useEffect(() => {
        if (isBuyer) {
            (async () => {
                try {
                    setCart(await getCart());
                } catch {
                    setCart(null);
                }
            })();
        }
    }, [isBuyer]);

    const refreshCart = useCallback(async () => {
        try {
            const updatedCart = await getCart();
            setCart(updatedCart);
            return updatedCart;
        } catch {
            setCart(null);
            return null;
        }
    }, []);

    const addToCart = async (programId: string, language: LanguageIsoCode, excludeEpisodeIds?: string[]) => {
        const updatedCart = await apiAddToCart(programId, language, excludeEpisodeIds);
        setCart(updatedCart);
    };

    const removeFromCart = async (programId: string, language: LanguageIsoCode) => {
        try {
            const updatedCart = await apiRemoveFromCart(programId, language);
            setCart(updatedCart);
        } catch {
            return;
        }
    };

    const updateCart = async (programId: string, currentLanguage: LanguageIsoCode, newLanguage: LanguageIsoCode) => {
        try {
            const updatedCart = await apiUpdateCart(programId, currentLanguage, newLanguage);
            setCart(updatedCart);
        } catch {
            return;
        }
    };

    return (
        <CartContext.Provider value={{ cart, refreshCart, addToCart, removeFromCart, updateCart }}>
            {children}
        </CartContext.Provider>
    );
};
